import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Checkbox, Drawer } from '@mui/material';
import swal from 'sweetalert';
import getServices from '../../Services/get-service';
import postService from '../../Services/post-service';
import putServices from '../../Services/put-service';
import Loading from '../SelectValue/Loading';
import ButtonPlus from '../SelectValue/ButtonPlus';

const drawerWidth = 650;

function ProfileDrawer({ open, onClose, type, onSaved, profile }) {
  const [name, setName] = useState('');
  const [ratePercentage, setRatePercentage] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  const isInsurance = type === 'insurance';
  const isEditing = Boolean(profile?.id);
  const title = `${isEditing ? 'Edit' : 'Add new'} ${isInsurance ? 'insurance' : 'tax'} profile`;
  const nameLabel = isInsurance ? 'Profile Name' : 'Tax Profile Name';
  const namePlaceholder = isInsurance ? 'e.g Life Coverage' : 'e.g VAT';
  const rateLabel = isInsurance ? 'Annual Rate (%)' : 'Rate (%)';

  useEffect(() => {
    if (!open) return;

    setName(profile?.name || '');
    setRatePercentage(profile?.ratePercentage !== undefined && profile?.ratePercentage !== null ? String(profile.ratePercentage) : '');
    setDescription(profile?.description || '');
    setIsActive(profile?.active ?? profile?.isActive ?? true);
  }, [open, profile]);

  const resetForm = () => {
    setName('');
    setRatePercentage('');
    setDescription('');
    setIsActive(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    const body = {
      name,
      ratePercentage: Number(ratePercentage),
      description,
    };

    const request = isEditing
      ? isInsurance
        ? putServices.updateInsuranceProfile(profile.id, { ...body, active: isActive })
        : putServices.updateTaxProfile(profile.id, { ...body, isActive })
      : isInsurance
        ? postService.createInsuranceProfile({ ...body, isActive })
        : postService.createTaxProfile({ ...body, isActive });

    request.then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          swal(response.data.description || 'Successful');
          resetForm();
          onSaved();
          onClose();
          return;
        }

        swal(response.data.description || `Unable to ${isEditing ? 'update' : 'create'} profile`);
      },
      (error) => {
        setLoading(false);
        swal(error.message || 'Something went wrong, try again');
      }
    );
  };

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { xs: '100%', sm: '80%', md: drawerWidth },
          backgroundColor: 'white',
        },
      }}
    >
      <Loading open={loading} />
      <div className="px-4 h-full flex flex-col">
        <div className="flex my-4 justify-between items-center px-2">
          <h1 className="text-xl md:text-2xl font-bold text-back-color2">{title}</h1>
          <div className="p-2 hover:bg-gray-100 rounded-full cursor-pointer" onClick={handleClose}>
            <CloseIcon />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-3 md:px-5 pt-8 flex-1 flex flex-col">
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm md:text-base font-medium text-gray-700">{nameLabel}</label>
              <input
                className="w-full bg-[#F7F8FB] outline-0 p-3 rounded-lg text-sm md:text-base placeholder:text-gray-400"
                placeholder={namePlaceholder}
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm md:text-base font-medium text-gray-700">{rateLabel}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full bg-[#F7F8FB] outline-0 p-3 rounded-lg text-sm md:text-base placeholder:text-gray-400"
                placeholder="0.00"
                required
                value={ratePercentage}
                onChange={(event) => setRatePercentage(event.target.value)}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm md:text-base font-medium text-gray-700">Description</label>
              <textarea
                className="border p-3 md:p-4 outline-0 rounded-lg w-full h-28 text-sm md:text-base transition-all resize-none"
                placeholder="Description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg bg-[#F3EDFF] px-3 py-3 text-left"
              onClick={() => setIsActive((currentValue) => !currentValue)}
            >
              <span className="text-sm md:text-base font-bold text-primary-color">Status</span>
              <span className={`relative h-8 w-14 rounded-full transition-colors ${isActive ? 'bg-[#344054]' : 'bg-gray-300'}`}>
                <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${isActive ? 'translate-x-7' : 'translate-x-1'}`} />
              </span>
            </button>
          </div>

          <div className="mt-auto flex my-8 justify-end border-t pt-6 mb-10 gap-3">
            <button type="button" onClick={handleClose} className="rounded-lg bg-white border px-4 py-2 font-bold text-primary-color">
              Discard
            </button>
            <button type="submit" disabled={loading} className="rounded-lg bg-violet-500 px-4 py-2 font-bold text-white disabled:opacity-60">
              {loading ? 'Saving...' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </Drawer>
  );
}

export default function ProfileManager({ type }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const isInsurance = type === 'insurance';
  const title = isInsurance ? 'Insurance profiles' : 'Tax profiles';
  const addLabel = isInsurance ? 'Add Insurance Profile' : 'Add Tax Profile';

  const fetchProfiles = useCallback(() => {
    setLoading(true);
    const request = isInsurance ? getServices.getInsuranceProfiles() : getServices.getTaxProfiles();

    request.then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setProfiles(response.data.data || []);
          return;
        }

        swal(response.data.description || 'Unable to fetch profiles');
      },
      (error) => {
        setLoading(false);
        swal(error.message || 'Unable to fetch profiles');
      }
    );
  }, [isInsurance]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return (
    <div>
      <Loading open={loading} />
      <ProfileDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingProfile(null);
        }}
        type={type}
        profile={editingProfile}
        onSaved={fetchProfiles}
      />

      <div className="mb-8 flex items-center justify-between gap-4">
        <h1 className="text-[#0A0F46] text-[20px] font-[500]">{title}</h1>
        <ButtonPlus
          name={addLabel}
          onClick={() => {
            setEditingProfile(null);
            setDrawerOpen(true);
          }}
        />
      </div>

      <div>
        <div className="hidden md:flex flex-row justify-between items-center bg-primary-bg rounded-t-md rounded-r-md border-primary-border border p-4">
          <Checkbox />
          <p className="text-text flex-1 text-center whitespace-normal break-words">Name</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Rate</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Status</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Action</p>
        </div>

        <div>
          {profiles.length === 0 && !loading ? (
            <div className="border-b border-gray-100 p-6 text-center text-gray-500">No profiles yet</div>
          ) : null}

          {profiles.map((profile) => (
            <div key={profile.id} className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-gray-100 p-4">
              <div className="hidden md:block">
                <Checkbox />
              </div>
              <div className="flex-1 text-left md:text-center break-words whitespace-normal min-w-0 px-1">
                <p className="md:hidden text-xs text-gray-400">Name</p>
                <p className="text-text font-medium">{profile.name}</p>
                {profile.description ? <p className="text-sm text-gray-500">{profile.description}</p> : null}
              </div>
              <div className="flex-1 text-left md:text-center break-words whitespace-normal min-w-0 px-1">
                <p className="md:hidden text-xs text-gray-400">Rate</p>
                <p className="text-text">{profile.ratePercentage}%</p>
              </div>
              <div className="flex-1 text-left md:text-center break-words whitespace-normal min-w-0 px-1">
                <p className="md:hidden text-xs text-gray-400">Status</p>
                <span className={`inline-flex rounded-lg px-3 py-2 text-sm ${profile.active ? 'bg-primary-bg text-text' : 'bg-red-50 text-red-400'}`}>
                  {profile.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex-1 flex justify-start md:justify-center text-[#44474E] break-words whitespace-normal min-w-0 px-1">
                <button
                  type="button"
                  title={`Edit ${isInsurance ? 'insurance' : 'tax'} profile`}
                  onClick={() => {
                    setEditingProfile(profile);
                    setDrawerOpen(true);
                  }}
                >
                  <EditIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
